import { useState } from "react"
import { Search, CheckCircle2, AlertCircle } from "lucide-react"
import { Button } from "@components/common/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@components/common/card"
import { Badge } from "@components/common/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@components/common/table"
import { Input } from "@components/common/input"
import { FINANCIAL_SERVER_URL, REQUEST_FOR_SIGNING_BOQ_ID_ENDPOINT } from "@pma/utils/api-routes"
import axios from "axios"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { useQuery } from "@tanstack/react-query"
import useTranslator from "@pma/hooks/use-translator"
import { useAuth } from "@pma/stores/auth-store"
import { cn, getUserFromId } from "@pma/utils/helpers"
import { DictType } from "@pma/types"
import { Avatar, AvatarFallback, AvatarImage } from "@components/common/avatar"
import { Link } from "react-router-dom"
import { USERS_PAGE_ROUTE } from "@pma/utils/page-routes"
import PmsLoader from "@components/common/loader"
import { ItemRequestSigningDialog } from "./item-request-signing-dialog"
import { formatCurrency } from "@pma/utils/utils"

interface BoQDetailsProps {
  boqId: string
}

export function SignBoQItemDetails({ boqId }: BoQDetailsProps) {
  const [searchQuery, setSearchQuery] = useState("")

  const { authConfig } = useAuth()

  const { translate, locale } = useTranslator()

  const isHebrew = locale === "he"

  const accessToken = parseCookies()[ACCESS_TOKEN]
  const organizationUsers = authConfig?.organizationUsers

  const getBoQItemsForSigning = async () => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + REQUEST_FOR_SIGNING_BOQ_ID_ENDPOINT,

      {
        boq_id: boqId,
      },
      {
        headers: {
          Authorization: "Bearer " + accessToken,
          "Content-Type": "application/json",
        },
      },
    )
    return response.data
  }

  const {
    data: forSigningBoqItems,
    isLoading: isLoadingSigningBoQs,
    refetch: refetchForSigningItems,
  } = useQuery({
    queryKey: ["for-signing-boq", boqId],
    queryFn: getBoQItemsForSigning,
  })

  const boqItems: DictType[] = forSigningBoqItems?.items || []
  const filteredItems = boqItems.filter(
    (item) =>
      item?._boq_item?.number?.includes(searchQuery) ||
      item?._boq_item?.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item?.new_item_data?.number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item?.new_item_data?.name?.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  const getStatusBadge = (item: DictType) => {
    if (item?.is_decline) {
      return (
        <Badge variant='disabled' className='flex items-center gap-1'>
          <span className='text-xs'>{translate("Declined")}</span>
        </Badge>
      )
    }
    if (item?.is_approved) {
      return (
        <Badge variant='default' className='flex items-center gap-1'>
          <CheckCircle2 className='h-3 w-3' /> {translate("Approved")}
        </Badge>
      )
    } else if (item?.is_signed) {
      return (
        <Badge variant='destructive' className='flex items-center gap-1'>
          <AlertCircle className='h-3 w-3' /> {translate("Signed")}
        </Badge>
      )
    } else {
      return (
        <Badge variant='secondary' className='flex items-center gap-1'>
          {translate("Pending")}
        </Badge>
      )
    }
  }

  return (
    <div className='space-y-6'>
      <Card>
        <CardHeader>
          <CardTitle>{translate("Sign BoQ Items")}</CardTitle>
          <CardDescription>{translate("View and sign individual BoQ items")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className='space-y-4'>
            <div className='flex flex-col justify-between gap-4 md:flex-row'>
              <div className='flex flex-col gap-2 md:flex-row'>
                <div className='relative'>
                  <Search
                    className={cn(
                      "absolute top-2.5 h-4 w-4 text-muted-foreground",
                      isHebrew ? "right-2.5" : "left-2.5",
                    )}
                  />
                  <Input
                    placeholder={translate("Search items") + "..."}
                    className={cn('w-full md:w-[300px]', isHebrew ? "pr-8": "pl-8")}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className='rounded-md border'>
              <div className='border-b last:border-b-0'>
                <div className='overflow-x-auto'>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className='w-[100px] cursor-pointer'>
                          <div className='flex items-center gap-1'>{translate("Number")}</div>
                        </TableHead>
                        <TableHead className='cursor-pointer'>
                          <div className='flex items-center gap-1'>{translate("Description")}</div>
                        </TableHead>
                        <TableHead className='w-[135px]'>{translate("Status")}</TableHead>

                        <TableHead className='w-[80px]'>{translate("Unit")}</TableHead>
                        <TableHead className='w-[120px] cursor-pointer text-right'>
                          <div className='flex items-center gap-1'>{translate("Requested By")}</div>
                        </TableHead>
                        <TableHead className='w-[160px] cursor-pointer text-right'>
                          <div className='flex items-center justify-end gap-1'>
                            {translate("Requested Amount")}
                          </div>
                        </TableHead>
                        <TableHead className='w-[100px] text-right'>{translate("Cost")}</TableHead>
                        <TableHead className='w-[100px]'>{translate("Actions")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    {filteredItems?.length > 0 && !isLoadingSigningBoQs && (
                      <TableBody>
                        {filteredItems?.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell className='font-mono text-sm'>
                              {item?._boq_item?.number || item?.new_item_data?.number}
                            </TableCell>
                            <TableCell>
                              <div>{item?._boq_item?.description || item?.new_item_data?.name}</div>
                            </TableCell>
                            <TableCell>{getStatusBadge(item)}</TableCell>

                            <TableCell>
                              {item?._boq_item?.units || item?.new_item_data?.units || "---"}
                            </TableCell>
                            <TableCell className='text-right'>
                              <Link to={USERS_PAGE_ROUTE + `/${item?.designer_id}`}>
                                <Button variant={"link"} size='sm' className='flex gap-2 p-0'>
                                  <Avatar className={`ml-[-10px] h-7 w-7`}>
                                    <AvatarImage
                                      src={
                                        getUserFromId(item?.requested_by_id, organizationUsers)
                                          .user_picture
                                          ? getUserFromId(item?.requested_by_id, organizationUsers)
                                              .user_picture.url
                                          : `${process.env.PUBLIC_URL}/usr.svg`
                                      }
                                      alt='Avatar'
                                    />
                                    <AvatarFallback className='uppercase'>
                                      {getUserFromId(item?.requested_by_id, organizationUsers)
                                        ?.first_name &&
                                        getUserFromId(item?.requested_by_id, organizationUsers)
                                          ?.first_name[0]}
                                      {getUserFromId(item?.requested_by_id, organizationUsers)
                                        ?.last_name &&
                                        getUserFromId(item?.requested_by_id, organizationUsers)
                                          ?.last_name[0]}
                                    </AvatarFallback>
                                  </Avatar>{" "}
                                  {getUserFromId(item?.requested_by_id, organizationUsers)?.name}
                                </Button>
                              </Link>
                            </TableCell>

                            <TableCell className='text-right'>{item?.requested_amount}</TableCell>
                            <TableCell className='text-right'>
                              {formatCurrency(
                                item?._boq_item?.cost || item?.new_item_data?.cost,
                                isHebrew,
                              )}
                            </TableCell>

                            <TableCell>
                              <div className='flex items-center gap-1'>
                                <ItemRequestSigningDialog
                                  requestItem={item}
                                  revalidate={refetchForSigningItems}
                                />
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    )}
                  </Table>
                  {isLoadingSigningBoQs && <PmsLoader />}
                  {filteredItems?.length < 1 && !isLoadingSigningBoQs && (
                    <div className='flex w-full items-center justify-center py-4 text-sm font-medium'>
                      <p>{translate("No items found")}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
        <CardFooter className='flex justify-between'>
          <div className='text-sm text-muted-foreground'>
            {translate("Showing")} {filteredItems?.length} {translate("of")} {boqItems?.length}{" "}
            {translate("items")}
          </div>
        </CardFooter>
      </Card>
    </div>
  )
}
